import React, { useRef, useState } from "react";

import { useUpload } from "../../hooks/useTranscription";
import { JobItem } from "../../services/transcription.service";

const JobBanner = ({ job, onReset }: { job: JobItem; onReset: () => void }) => (
  <div
    className={`alert border-2 ${job.status === "COMPLETED" ? "alert-success" : "alert-warning"}`}
  >
    <div className="d-flex justify-content-between align-items-center">
      <span>
        <strong>Job accepted:</strong> {job.status}
        <small className="ms-2 font-monospace">({job.job_id.slice(0, 8)})</small>
      </span>
      <button className="btn btn-sm btn-outline-dark fw-bold" onClick={onReset}>
        New job
      </button>
    </div>
  </div>
);

interface UploadFormProps {
  loading: boolean;
  fileRef: React.RefObject<HTMLInputElement>;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

const UploadForm = ({ loading, fileRef, onFileChange, onSubmit }: UploadFormProps) => (
  <form onSubmit={onSubmit}>
    <div className="input-group input-group-lg">
      <input
        type="file"
        className="form-control"
        ref={fileRef}
        accept="audio/*"
        onChange={onFileChange}
        disabled={loading}
      />
      <button type="submit" className="btn btn-success fw-bold px-4" disabled={loading}>
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm"></span>
          </>
        ) : (
          "Submit Job"
        )}
      </button>
    </div>
    <div className="form-text mt-2 text-muted">
      Select an audio file to begin the transcription process.
    </div>
  </form>
);

interface UploadCardProps {
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
}

export const UploadCard = ({ setRefreshTrigger }: UploadCardProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formErrorWarning, setFormErrorWarning] = useState(false);

  const { submitJob, loading, currentJob, error, reset } = useUpload();

  const handleSubmit = async (e: React.FormEvent) => {
    setFormErrorWarning(false);
    e.preventDefault();
    if (!selectedFile) {
      setFormErrorWarning(true);
      return;
    }
    const jobId = await submitJob(selectedFile);
    if (jobId) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setRefreshTrigger((t: number) => t + 1);
      reset();
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null);
  };

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body bg-light">
        {(formErrorWarning || error) && (
          <div className="alert alert-danger">{error ?? "No file!"}</div>
        )}
        {currentJob ? (
          <JobBanner job={currentJob} onReset={reset} />
        ) : (
          <UploadForm
            loading={loading}
            fileRef={fileInputRef}
            onFileChange={onFileChange}
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          />
        )}
      </div>
    </div>
  );
};
